import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import { logoutUser } from "../../redux/slices/authSlice";
import { changePassword } from "../../redux/apis/Businessuser/changeBusinessUserPassword";

const ChangePasswordPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    user,
    token,
    isAuthenticated,
    loading: authLoading,
  } = useSelector((state) => state.auth);

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // 🔥 HINT CONTROL
  const [showHint, setShowHint] = useState(false);

  // 🔥 PASSWORD RULES
  const rules = [
    { regex: /.{8,}/, message: "At least 8 characters" },
    { regex: /[A-Z]/, message: "At least one uppercase letter" },
    { regex: /[a-z]/, message: "At least one lowercase letter" },
    { regex: /\d/, message: "At least one number" },
  ];

  const [passwordRules, setPasswordRules] = useState([]);

  useEffect(() => {
    if (!isAuthenticated) navigate("/login");
  }, [isAuthenticated, navigate]);

  // 🔥 PASSWORD VALIDATION
  const handlePasswordChange = (value) => {
    setNewPassword(value);

    const updated = rules.map((rule) => ({
      ...rule,
      valid: rule.regex.test(value),
    }));

    setPasswordRules(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!oldPassword || !newPassword || !confirmPassword) {
      return setError("All fields are required");
    }

    if (newPassword !== confirmPassword) {
      return setError("Passwords do not match");
    }

    if (oldPassword === newPassword) {
      return setError("New password must be different");
    }

    const isValid = passwordRules.every((r) => r.valid);
    if (!isValid) {
      return setError("Password does not meet security rules");
    }

    setLoading(true);

    const result = await dispatch(
      changePassword({
        userId: user._id,
        oldPassword,
        newPassword,
        token,
      }),
    );

    if (changePassword.fulfilled.match(result)) {
      setSuccessMessage("Password changed successfully");

      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");

      localStorage.removeItem("token");
      dispatch(logoutUser());

      setTimeout(() => navigate("/login"), 2000);
    } else {
      setError(result.payload || "Password change failed");
    }

    setLoading(false);
  };

  if (authLoading) {
    return <div className="text-center mt-10">Checking auth status...</div>;
  }

  return (
    <div className="p-8 bg-gray-100 min-h-screen flex justify-center items-center">
      <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
        <h2 className="text-2xl font-semibold mb-6">🔒 Change Password</h2>

        {successMessage && (
          <div className="text-green-600 mb-3">{successMessage}</div>
        )}

        {error && <div className="text-red-600 mb-3">{error}</div>}

        <form onSubmit={handleSubmit}>
          {/* OLD PASSWORD */}
          <input
            type="password"
            placeholder="Old Password"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            className="w-full p-2 border mb-3"
          />

          {/* NEW PASSWORD */}
          <input
            type="password"
            placeholder="New Password"
            value={newPassword}
            onFocus={() => setShowHint(true)}
            onBlur={() => setShowHint(false)}
            onChange={(e) => handlePasswordChange(e.target.value)}
            className="w-full p-2 border mb-1"
          />

          {/* 🔥 STATIC HINT (always visible) */}
          <p className="text-xs text-gray-500 mb-2">
            Use 8+ characters, uppercase, lowercase & number
          </p>

          {/* 🔥 LIVE RULE HINT */}
          {showHint && (
            <div className="mb-3 text-sm space-y-1">
              {passwordRules.map((rule, i) => (
                <p
                  key={i}
                  className={rule.valid ? "text-green-600" : "text-gray-400"}
                >
                  {rule.message}
                </p>
              ))}
            </div>
          )}

          {/* CONFIRM PASSWORD */}
          <input
            type="password"
            placeholder="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full p-2 border mb-4"
          />

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white p-2 rounded"
          >
            {loading ? "Changing..." : "Change Password"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
